<template>
  <div>
    <template v-if="IsCategoryPage">
      <a-lazy-hydrate when-visible>
        <a-breadcrumbs v-bind="breadcrumbsProps">
          <a-visibility
            v-if="isDisclaimerVisible"
            show
            :from="$breakpoint.desktopMd"
          >
            <a-disclaimer
              :layout-ordering="$options.consts.disclaimerLayoutOrdering"
            />
          </a-visibility>
        </a-breadcrumbs>
      </a-lazy-hydrate>

      <a-paginated-news-list
        ref="paginatedNewsList"
        :pagination="pagination"
        :pages-count="pagesCount"
        :key="forceRerenderKey"
        page-type="category"
        :categories-strip="CategoriesStrip"
        :category-name="CategoryName"
        :most-viewed-articles="mostViewedArticles"
        :all-articles="articles"
        :in-progress="isRequestInProgress"
        @get-new-chunk="getNextArticleChunk"
      >
        <template v-if="isDisclaimerVisible" v-slot:disclaimer>
          <a-visibility
            v-if="isDisclaimerVisible"
            show
            :to="$breakpoint.desktopSm"
          >
            <a-disclaimer
              :layout-ordering="$options.consts.disclaimerLayoutOrdering"
              :bottom-space-offset="disclaimerBottomOffset"
            />
          </a-visibility>
        </template>
      </a-paginated-news-list>
      <a-sticky-banner
        :sticky-position="$options.consts.STICKY_BANNER_POSITION.BOTTOM"
        :banner-settings="
          $options.consts.BANNER_SETTINGS.ALL_NEWS.STICKY_TO_BOTTOM
        "
      />
      <a-pagination-links-for-google
        :page="pagination.page"
        :pages-count="pagesCount"
        :non-paginated-url="canonicalUrlWithoutPagination"
      />
      <a-url-pagination
        :item-count="articles.length"
        :pagination.sync="pagination"
        :initial-page="initialPage"
        :pages-count="pagesCount"
        :item-selector="$options.consts.ARTICLE_SLOT_SELECTOR"
        :non-paginated-url="canonicalUrlWithoutPagination"
      />
    </template>
    <nuxt-child v-else />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGOUT,
  METHOD_NAME_TO_RUN_ON_LOGIN
} from '@/utils/mixins/run-method-on-login-logout'
import { ARTICLE_SLOT_CLASS_NAME } from 'shared/APaginatedNewsList/enums'
import { ROUTE_NAME } from '@/utils/enums/routes'
import { PAGE_SCHEME_TYPE } from '@/utils/enums/pageSchemes'
import ABreadcrumbs, { BREADCRUMB_NAMING } from 'shared/ABreadcrumbs'
import { getPageNumberFromRoute } from '@/plugins/helper'
import { applyFallback } from '@/utils/helpers'
import AStickyBanner from 'shared/AStickyBanner'
import APaginatedNewsList from 'shared/APaginatedNewsList'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import AUrlPagination from 'shared/AUrlPagination'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import { LAYOUT as DISCLAIMER_LAYOUT } from 'shared/ADisclaimer/enums'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { generateCategoryUrl } from '@/utils/helpers/url-generators'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { handleNextChunks } from '@/utils/helpers/handle-duplicates'
import { pick } from 'ramda'

/**
 * How many news should be taken from back-end for news by category
 * @type {number}
 */
const DEFAULT_TAKE = 25

async function getCategoryNewsData({
  ctx,
  route,
  store,
  handleScrollTop = true
}) {
  const initialPage = getPageNumberFromRoute(ctx.route)
  const { category } = route.params

  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }

  const categorySlug = category.toLowerCase()

  const dispatch = store.dispatch

  const requestCategoryArticlesPromise = dispatch(
    'articles/requestArticleByCategory',
    {
      categorySlug,
      ...pagination
    }
  )
  const promises = [
    dispatch('articles/requestCategoryPagePinnedSubcategories', {
      categorySlug
    }),
    dispatch('articles/requestMostViewedArticles', {
      isSponsored: store.getters['categories/checkIfThoughtLeadershipSlug'](
        categorySlug
      )
    })
  ]

  const {
    Articles,
    Category: { Name: CategoryName, Slug: CategorySlug },
    PagesCount: pagesCount
  } = await requestCategoryArticlesPromise

  const [CategoriesStrip, { mostViewedArticles }] = await Promise.all(promises)

  if (pagesCount && initialPage > pagesCount) {
    const { throwPageNotFoundError } = await import('@/plugins/error')

    return throwPageNotFoundError(ctx, 'CategoryPage')
  }

  if (handleScrollTop) {
    handleScrollToTop()
  }

  const { MOST_VIEWED_ARTICLES_MODEL } = await import('enums/responseModels')

  return {
    articles: Articles,
    mostViewedArticles: applyFallback(
      mostViewedArticles,
      MOST_VIEWED_ARTICLES_MODEL
    ),
    CategoriesStrip,
    initialPage,
    pagesCount,
    pagination: {
      take: DEFAULT_TAKE,
      page: initialPage - 1
    },
    CategoryName,
    CategorySlug
  }
}

function handleScrollToTop() {
  if (process.browser) window.scrollTo(0, 0)
}

function generateDefaultCategoryNewsContentDataModel() {
  return {
    articles: [],
    mostViewedArticles: [],
    CategoriesStrip: [],
    CategoryName: ''
  }
}

export default {
  name: 'CategoryPage',
  scrollToTop: true,
  mixins: [
    mixins.refreshDataHandler,
    mixins.runMethodOnLoginLogout,
    mixins.setPageLoaded,
    hydrationHelpers,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE
      }
    }),
    mixins.delayedContentRefresh
  ],
  components: {
    APaginatedNewsList,
    ABreadcrumbs,
    AStickyBanner,
    APaginationLinksForGoogle,
    ADisclaimer: hydrateWhenVisible(() => import('shared/ADisclaimer'), {
      props: ['layout-ordering', 'bottom-space-offset']
    }),
    AUrlPagination
  },
  consts: {
    BANNER_SETTINGS,
    STICKY_BANNER_POSITION,
    disclaimerLayoutOrdering: [
      DISCLAIMER_LAYOUT.BOTTOM_LEFT,
      DISCLAIMER_LAYOUT.TOP_LEFT
    ],
    disclaimerBottomOffsetMobile: 50, // bottom banner on mobile
    ARTICLE_SLOT_SELECTOR: `.${ARTICLE_SLOT_CLASS_NAME}`
  },
  head() {
    return this.$generateMetaTags({
      titleParam: this.CategoryName,
      descriptionParam: this.CategoryName,
      ...(this.IsCategoryPage
        ? { schemes: [{ type: PAGE_SCHEME_TYPE.ORGANIZATION }] }
        : {}),
      additionalLinks: [
        /** Feed pages should exist only for categories (and should not exist for subcategories and articles) **/
        ...(this.IsCategoryPage
          ? [
              {
                rel: 'alternate',
                type: 'application/rss+xml',
                title: `Finance Magnates | Financial and business news » ${this.CategoryName} Category Feed`,
                href: `${this.$env.DOMAIN_URL}/${this.categorySlug}/feed`
              }
            ]
          : [])
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath, name } = ctx.route

    if (name !== ROUTE_NAME.CATEGORY) {
      return { IsCategoryPage: false }
    }

    try {
      const categoryNewsData = await getCategoryNewsData({
        ctx,
        route: ctx.route,
        store: ctx.store
      })

      const canonicalUrlWithoutPagination = generateCategoryUrl(
        { CategorySlug: categoryNewsData.CategorySlug },
        ctx
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: ctx.route.path
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        ...categoryNewsData,
        canonicalUrl,
        canonicalUrlWithoutPagination,
        IsCategoryPage: true,
        forceRerenderKey: Math.random().toString()
      }
    } catch (err) {
      const { throwPageNotFoundError } = await import('@/plugins/error')

      throwPageNotFoundError(ctx, 'CategoryPage')
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultCategoryNewsContentDataModel(),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      CategorySlug: '',
      IsCategoryPage: null,
      pagesCount: 0,
      inProgress: false,
      initialPage,
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      shift: 0,
      forceRerenderKey: null,
      disclaimerBottomOffsetByBreakpoint: {
        [this.$breakpoint.mobile]: this.$options.consts
          .disclaimerBottomOffsetMobile
      }
    }
  },
  computed: {
    ...mapGetters({
      checkIfThoughtLeadershipSlug: 'categories/checkIfThoughtLeadershipSlug'
    }),
    isRequestInProgress() {
      return this.inProgress || this.isPendingContentRefresh
    },
    breadcrumbsDynamicNames() {
      return { [ROUTE_NAME.CATEGORY]: this.CategoryName }
    },
    breadcrumbsProps() {
      return {
        breadcrumbsDynamicNames: this.breadcrumbsDynamicNames,
        breadcrumbsNaming: BREADCRUMB_NAMING.KEEP_AS_IS
      }
    },
    categorySlug() {
      return this.$route.params.category.toLowerCase()
    },
    isDisclaimerVisible() {
      return this.checkIfThoughtLeadershipSlug(this.categorySlug)
    },
    disclaimerBottomOffset() {
      return (
        this.$_hydrationHelpers_getValueByBreakpoint(
          this.disclaimerBottomOffsetByBreakpoint
        ) || 0
      )
    }
  },
  methods: {
    ...mapActions({
      requestArticleByCategory: 'articles/requestArticleByCategory'
    }),
    async getArticles(pagination) {
      try {
        const { Articles, PagesCount } = await this.requestArticleByCategory({
          categorySlug: this.categorySlug,
          ...pagination
        })
        this.pagesCount = PagesCount

        return Articles
      } catch (err) {
        this.$errorHandler(err, this, {
          showErrorPage: true,
          showMessage: false
        })
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      handleNextChunks({
        shift: this.shift,
        pagination: this.pagination,
        pagesCount: this.pagesCount,
        setInProgressTrueFn: () => {
          this.inProgress = true
        },
        setInProgressFalseFn: () => {
          this.inProgress = false
        },
        setShiftFn: newVal => {
          this.shift = newVal
        },
        requestNextChunkFn: this.getArticles.bind(this),
        addNewArticlesFn: async newArticles => {
          this.articles = [...this.articles, ...newArticles]
          await this.$nextTick()
        },
        getExistingArticleIdsFn: () => this.articles.map(({ id }) => id)
      })
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE](payload) {
      this.articles = this.$_refreshDataHandler_updateIsSavedValue(payload)
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE](payload) {
      this.articles = this.$_refreshDataHandler_updateIsFollowedTagValue(
        payload
      )
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.articles = this.$_refreshDataHandler_resetActionIconValues()
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const {
        Article,
        Tag
      } = await this.$_refreshDataHandler_getSavedIdsOfEntities([
        this.$_refreshDataHandler_savedEntity.ARTICLE,
        this.$_refreshDataHandler_savedEntity.TAG
      ])
      this.articles = this.$_refreshDataHandler_updateArticleSavedEntities({
        articleIds: Article,
        tagIds: Tag
      })
    },
    refreshContentForCategoryPage() {
      if (!this.IsCategoryPage) return

      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultCategoryNewsContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getCategoryNewsData({
            ctx: this,
            route: this.$route,
            store: this.$store,
            handleScrollTop: false
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['lastModified'],
        dataChangedCallbackFn: newData => {
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
          this.$refs.paginatedNewsList.rerenderNonHydratedComponents()
        }
      })
    }
  },
  mounted() {
    this.refreshContentForCategoryPage()
  }
}
</script>
